import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatNumber } from '../utils/numberFormatter';
import { fetchChannelComparison, searchCompetitorChannels } from '../store/actions/competitorChannelsActions';
import { MobileSearchIcon } from '../assets/svgs/svgs';

const ChannelComparison = ({ competitorChannels = [] , comparisonStats = []}) => {
  const dispatch = useDispatch();
  const competitorChannelsState = useSelector((state) => state.competitorChannels);
  const comparisonStatsFromRedux = competitorChannelsState?.channelComparison;
  const finalComparisonStats = comparisonStatsFromRedux || comparisonStats;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const dropdownRef = useRef(null);

  const comparisonStatsArray = Array.isArray(finalComparisonStats)
    ? finalComparisonStats
    : [];

  const isLoading = competitorChannelsState?.comparisonLoading || false;
  const hasComparisonData = comparisonStatsArray.length > 0 && selectedChannel;

  const yourViews = comparisonStatsArray[0]?.viewCount || 0;
  const otherViews = comparisonStatsArray[1]?.viewCount || 0;
  const totalViews = yourViews + otherViews;
  
  const yourPercentage = totalViews > 0 ? yourViews / totalViews : 0;
  const minVisiblePercentage = 0.03;
  const displayYourPercentage = yourPercentage > 0 && yourPercentage < minVisiblePercentage 
    ? minVisiblePercentage 
    : yourPercentage;
  
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  
  const getPieSlicePath = (startAngle, endAngle) => {
    const start = {
      x: centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180),
      y: centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180)
    };
    const end = {
      x: centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180),
      y: centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180)
    };
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${centerX} ${centerY} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
  };
  
  const yourAngle = displayYourPercentage * 360;
  const isFullCircle = displayYourPercentage >= 0.999 || yourAngle >= 359.9;
  const isEmpty = totalViews === 0 || yourAngle <= 0.1;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchClick = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsSearching(true);
    setIsDropdownOpen(true);
    try {
      const results = await dispatch(searchCompetitorChannels(searchQuery.trim()));
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Error searching channels:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleChannelSelect = (channel) => {
    const channelUrl = channel.url || channel.channelUrl || channel.id;
    const channelName = channel.channelName || channel.name || channel.title || channelUrl;

    setSelectedChannel({
      url: channelUrl,
      channelName: channelName,
      thumbnail: channel.thumbnail || '',
      ...channel
    });
    setIsDropdownOpen(false);
    setSearchQuery(channelName);
    setSearchResults([]);

    if (channelUrl) {
      dispatch(fetchChannelComparison(channelUrl));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <h4 className="text-lg font-semibold text-gray-900">Channel Comparison</h4>
      </div>

      {/* Search Input */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearchClick();
              }
            }}
            onFocus={() => {
              if (searchResults.length > 0) {
                setIsDropdownOpen(true);
              }
            }}
            placeholder="Compare With..."
            className="w-full px-4 py-2.5 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#78350F]/20 focus:border-[#78350F] bg-white"
          />
          <button
            type="button"
            onClick={handleSearchClick}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            disabled={isSearching}
          >
            {isSearching ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#78350F]"></div>
            ) : (
              <MobileSearchIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>

        {/* Search Results Dropdown */}
        {isDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
            <div className="max-h-60 overflow-y-auto">
              {isSearching ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#78350F]"></div>
                  <span>Searching...</span>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {searchQuery.trim() ? 'No channels found' : 'Type to search channels'}
                </div>
              ) : (
                searchResults.map((channel, index) => {
                  const channelName = channel.title || channel.channelName || channel.url || '';
                  const channelUrl = channel.channelUrl || channel.url || channel.channelName || '';
                  const channelThumbnail = channel.thumbnail || '';
                  return (
                    <button
                      key={channelUrl || index}
                      type="button"
                      onClick={() => handleChannelSelect(channel)}
                      className="w-full px-4 py-3 text-left text-sm hover:bg-[#78350F]/5 transition-colors duration-150 flex items-center gap-3"
                    >
                      {channelThumbnail ? (
                        <img
                          src={channelThumbnail}
                          alt={channelName}
                          className="w-10 h-10 object-cover rounded-full flex-shrink-0 border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center">
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <span className="truncate flex-1 text-gray-700">{channelName}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {!selectedChannel ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium text-sm">Select a channel to compare</p>
          <p className="text-xs text-gray-500 mt-1">Search and select a channel above</p>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#78350F] mb-3"></div>
          <p className="text-gray-600 text-sm">Loading comparison...</p>
        </div>
      ) : hasComparisonData ? (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-[#EFEFEF] rounded-md p-3">
              <p className="text-xs text-gray-400 mb-1 text-left">Channel Views</p>
              <p className="text-sm font-bold text-gray-900 mb-2 text-left">{formatNumber(comparisonStatsArray[0]?.viewCount || 0)}</p>
              <div className="space-y-0.5 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Your</span>
                  <span className="text-[#78350F] font-semibold">{formatNumber(comparisonStatsArray[0]?.viewCount || 0)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Other</span>
                  <span className="text-gray-600">+{formatNumber((comparisonStatsArray[1]?.viewCount || 0) - (comparisonStatsArray[0]?.viewCount || 0))}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#EFEFEF] rounded-md p-3">
              <p className="text-xs text-gray-400 mb-1 text-left">Subscribers</p>
              <p className="text-sm font-bold text-gray-900 mb-2 text-left">{formatNumber(comparisonStatsArray[0]?.subscriberCount || 0)}</p>
              <div className="space-y-0.5 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Your</span>
                  <span className="text-[#78350F] font-semibold">{formatNumber(comparisonStatsArray[0]?.subscriberCount || 0)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Other</span>
                  <span className="text-gray-600">+{formatNumber((comparisonStatsArray[1]?.subscriberCount || 0) - (comparisonStatsArray[0]?.subscriberCount || 0))}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#EFEFEF] rounded-md p-3">
              <p className="text-xs text-gray-400 mb-1 text-left">Video Count</p>
              <p className="text-sm font-bold text-gray-900 mb-2 text-left">{formatNumber(comparisonStatsArray[0]?.videoCount || 0)}</p>
              <div className="space-y-0.5 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Your</span>
                  <span className="text-[#78350F] font-semibold">{formatNumber(comparisonStatsArray[0]?.videoCount || 0)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Other</span>
                  <span className="text-gray-600">+{formatNumber((comparisonStatsArray[1]?.videoCount || 0) - (comparisonStatsArray[0]?.videoCount || 0))}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center py-2 mb-4">
            <div className="w-40 h-40 lg:w-48 lg:h-48">
              <svg className="w-full h-full" viewBox="0 0 200 200">
                {isEmpty ? (
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    fill="#E7E5E4"
                  />
                ) : isFullCircle ? (
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    fill="#78350F"
                  />
                ) : (
                  <>
                    {yourAngle > 0 && (
                      <path
                        d={getPieSlicePath(0, yourAngle)}
                        fill="#78350F"
                      />
                    )}
                    <path
                      d={getPieSlicePath(yourAngle, 360)}
                      fill="#E7E5E4"
                    />
                  </>
                )}
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#78350F]"></div>
                <span className="text-xs text-gray-600">Views</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{formatNumber(yourViews)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#E7E5E4]"></div>
                <span className="text-xs text-gray-600">Views</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{formatNumber(otherViews)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-gray-600 text-sm">No comparison data available</p>
          <p className="text-xs text-gray-500 mt-1">Try selecting a different channel</p>
        </div>
      )}
    </div>
  );
};

export default ChannelComparison;
